import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Link as MuiLink,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TextFieldFormik from 'src/components/TextFieldFormik';
import ButtonV1 from 'src/components/ModalDialogMUI6/styled/ButtonV1';
import {
  getTelnyxMigrationPinStatus,
  verifyTelnyxMigrationPin,
  resendTelnyxMigrationPin,
} from 'src/actions/accountSettingsActions';
import {
  sanitizePin,
  isValidPin,
  secondsUntil,
  formatCooldown,
  toEpochMs,
} from 'src/helpers/telnyxMigrationPin';
import COPY from 'src/components/TelnyxMigrationPin/copy';

const PHASE = {
  LOADING: 'loading',
  ENTRY: 'entry',
  SUCCESS: 'success',
  NOTHING: 'nothing',
  PENDING: 'pending',
};

// The texting-move code form: 6-digit field, Verify, "Send a new code" with
// a cooldown, remaining-sends hint, inline errors and a success state. Used
// inside TelnyxMigrationPinModal and full-page at /app/verify.
//
// It loads its own status on mount (cooldown, resends left, masked phone).
// Before the code is sent (status pending) it says the code is on its way.
// A 404 or an already-verified status means there is nothing to do: it
// shows that and calls onNothingToDo so a host modal can close itself.
function TelnyxMigrationPinForm({
  maskedPhone: maskedPhoneFromLogin = null,
  showTitle = true,
  onVerified = () => { },
  onNothingToDo = () => { },
}) {
  const dispatch = useDispatch();
  const mountedRef = useRef(true);
  const pinInputRef = useRef(null);

  // Callbacks held in refs so the mount-only status load never re-runs
  // because a parent passed a new function identity.
  const onVerifiedRef = useRef(onVerified);
  const onNothingToDoRef = useRef(onNothingToDo);
  onVerifiedRef.current = onVerified;
  onNothingToDoRef.current = onNothingToDo;

  const [phase, setPhase] = useState(PHASE.LOADING);
  const [maskedPhone, setMaskedPhone] = useState(maskedPhoneFromLogin);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [canResendAt, setCanResendAt] = useState(null);
  const [resendsLeftToday, setResendsLeftToday] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendSent, setResendSent] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const showNothingToDo = useCallback(() => {
    setPhase(PHASE.NOTHING);
    onNothingToDoRef.current();
  }, []);

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setPhase(PHASE.LOADING);
    }
    const result = await dispatch(getTelnyxMigrationPinStatus());
    if (!mountedRef.current) {
      return;
    }
    const { ok, status, data } = result || {};
    if (ok && data?.pending === true) {
      setMaskedPhone(data?.maskedPhone || null);
      setPhase(PHASE.PENDING);
      return;
    }
    if (status === 404 || (ok && (data?.verified === true || data?.required === false))) {
      showNothingToDo();
      return;
    }
    if (ok) {
      if (data?.maskedPhone) {
        setMaskedPhone(data.maskedPhone);
      }
      setCanResendAt(toEpochMs(data?.canResendAt));
      setResendsLeftToday(typeof data?.resendsLeftToday === 'number' ? data.resendsLeftToday : null);
    }
    // A failed status load still shows the field: the code can be entered
    // without knowing the resend cooldown.
    setPhase(PHASE.ENTRY);
  }, [dispatch, showNothingToDo]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // 1-second tick that powers the resend countdown. Runs only while a
  // cooldown is in the future.
  const cooldownSeconds = secondsUntil(canResendAt, nowTick);
  useEffect(() => {
    if (!canResendAt || secondsUntil(canResendAt) <= 0) {
      return undefined;
    }
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [canResendAt]);

  useEffect(() => {
    if (canResendAt && cooldownSeconds === 0) {
      setCanResendAt(null);
    }
  }, [canResendAt, cooldownSeconds]);

  useEffect(() => {
    if (phase === PHASE.ENTRY && pinInputRef.current) {
      pinInputRef.current.focus();
    }
  }, [phase]);

  const handlePinChange = (event) => {
    setPin(sanitizePin(event?.target?.value));
    if (pinError) {
      setPinError(null);
    }
  };

  const handleVerify = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (isVerifying || !isValidPin(pin)) {
      return;
    }
    setIsVerifying(true);
    setPinError(null);
    const result = await dispatch(verifyTelnyxMigrationPin(pin));
    if (!mountedRef.current) {
      return;
    }
    setIsVerifying(false);

    const { ok, status, data } = result || {};
    if (ok && data?.verified === true) {
      setPhase(PHASE.SUCCESS);
      onVerifiedRef.current(data);
      return;
    }
    if (status === 404) {
      showNothingToDo();
      return;
    }

    setPinError(data?.error || COPY.genericError);
    if (data?.code === 'bad_pin') {
      // Clear so a wrong code does not have to be backspaced six times.
      setPin('');
      if (pinInputRef.current) {
        pinInputRef.current.focus();
      }
      return;
    }
    if (status === 409) {
      // The server's state moved on (for example the code was already
      // accepted elsewhere). Re-read it quietly; the error stays visible
      // unless the refresh shows there is nothing left to do.
      loadStatus({ silent: true });
    }
  };

  const handleResend = async () => {
    if (isResending || cooldownSeconds > 0) {
      return;
    }
    setIsResending(true);
    setResendError(null);
    setResendSent(false);
    const result = await dispatch(resendTelnyxMigrationPin());
    if (!mountedRef.current) {
      return;
    }
    setIsResending(false);

    const { ok, status, data } = result || {};
    if (ok && data?.sent) {
      setCanResendAt(toEpochMs(data?.canResendAt));
      setNowTick(Date.now());
      if (typeof data?.resendsLeftToday === 'number') {
        setResendsLeftToday(data.resendsLeftToday);
      }
      setResendSent(true);
      return;
    }
    if (status === 404) {
      showNothingToDo();
      return;
    }
    if (status === 429) {
      const retryAfterSeconds = Number(data?.retryAfterSeconds);
      if (retryAfterSeconds > 0) {
        setCanResendAt(Date.now() + retryAfterSeconds * 1000);
        setNowTick(Date.now());
      }
    }
    setResendError(data?.error || COPY.genericError);
  };

  if (phase === PHASE.LOADING) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (phase === PHASE.NOTHING) {
    return (
      <Box textAlign="center" py={2}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {COPY.nothingToDo}
        </Typography>
        <MuiLink component={RouterLink} to="/app" underline="hover" variant="body1">
          {COPY.nothingToDoLink}
        </MuiLink>
      </Box>
    );
  }

  if (phase === PHASE.PENDING) {
    return (
      <Box
        textAlign="center"
        py={2}
        display="flex"
        flexDirection="column"
        gap={1.5}
        data-testid="telnyx-migration-pin-pending"
      >
        <Typography variant="h4">
          {COPY.pendingTitle}
        </Typography>
        <Typography variant="body1">
          {COPY.pendingBody(maskedPhone)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {COPY.pendingWrongNumber}
        </Typography>
        <MuiLink component={RouterLink} to="/app" underline="hover" variant="body1">
          {COPY.nothingToDoLink}
        </MuiLink>
      </Box>
    );
  }

  if (phase === PHASE.SUCCESS) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        gap={1.5}
        py={2}
        data-testid="telnyx-migration-pin-success"
      >
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 48 }} />
        <Typography variant="body1">
          {COPY.success}
        </Typography>
      </Box>
    );
  }

  const noResendsLeft = resendsLeftToday === 0;

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleVerify}
      display="flex"
      flexDirection="column"
      gap={2}
      data-testid="telnyx-migration-pin-form"
    >
      {showTitle && (
        <Typography variant="h4" textAlign="center">
          {COPY.title}
        </Typography>
      )}

      <Typography variant="body1">
        {COPY.body(maskedPhone)}
      </Typography>

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        flexWrap="wrap"
        gap={1}
      >
        <Box sx={{ width: 220 }}>
          <TextFieldFormik
            name="otpPin"
            label={COPY.fieldLabel}
            value={pin}
            handleChange={handlePinChange}
            handleBlur={() => { }}
            touchedValue={Boolean(pinError)}
            errorValue={pinError}
            disabled={isVerifying}
            inputRef={pinInputRef}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6,
              autoComplete: 'one-time-code',
              'aria-label': COPY.fieldLabel,
            }}
          />
        </Box>
        <ButtonV1
          type="submit"
          styleType="ok"
          disabled={!isValidPin(pin) || isVerifying}
        >
          {isVerifying ? COPY.verifyingButton : COPY.verifyButton}
        </ButtonV1>
      </Box>

      <Box textAlign="center">
        {noResendsLeft && (
          <Typography variant="body2" color="text.secondary">
            {COPY.resendNoneLeft}
          </Typography>
        )}
        {!noResendsLeft && cooldownSeconds > 0 && (
          <Typography variant="body2" color="text.secondary">
            {COPY.resendCooldown(formatCooldown(cooldownSeconds))}
          </Typography>
        )}
        {!noResendsLeft && cooldownSeconds === 0 && (
          <MuiLink
            component="button"
            type="button"
            onClick={handleResend}
            disabled={isResending}
            underline="hover"
            variant="body2"
            sx={{ verticalAlign: 'baseline' }}
          >
            {isResending ? COPY.resendSending : COPY.resendLink}
          </MuiLink>
        )}
        {!noResendsLeft && typeof resendsLeftToday === 'number' && (
          <Typography variant="body2" color="text.secondary">
            {COPY.resendsLeft(resendsLeftToday)}
          </Typography>
        )}
        {resendSent && !resendError && (
          <Typography variant="body2" sx={{ color: 'success.dark' }}>
            {COPY.resendSent}
          </Typography>
        )}
        {resendError && (
          <Typography variant="body2" color="error">
            {resendError}
          </Typography>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" textAlign="center">
        {COPY.help}
      </Typography>
    </Box>
  );
}

TelnyxMigrationPinForm.propTypes = {
  maskedPhone: PropTypes.string,
  showTitle: PropTypes.bool,
  onVerified: PropTypes.func,
  onNothingToDo: PropTypes.func,
};

export default TelnyxMigrationPinForm;
